/**
 * Analytics helpers — pushes GA4 / GTM Enhanced Ecommerce events
 * via window.dataLayer (which is initialised by the GTM snippet in index.html).
 *
 * No-op if dataLayer isn't loaded yet (e.g., adblockers) — never throws.
 */

const push = (payload) => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  try {
    // Clear previous ecommerce object to avoid sticky values between events
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);
  } catch (err) {
    console.debug('[analytics] dataLayer push failed:', err?.message);
  }
};

const toItem = (item, idx = 0) => ({
  item_id: item.product_id || item.id || 'unknown',
  item_name: item.name || item.product_name || 'Produkt',
  price: Number(item.price) || 0,
  quantity: Number(item.quantity) || 1,
  item_category: item.category || undefined,
  item_variant: item.size || item.color || undefined,
  index: idx,
});

export const trackPageView = (path) => {
  push({ event: 'page_view', page_path: path });
  // Also ping our own backend (first-party analytics)
  try {
    let sid = sessionStorage.getItem('analytics_sid');
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('analytics_sid', sid);
    }
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${backendUrl}/api/track/view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path, sid, referrer: document.referrer || '' }),
      keepalive: true,
    }).catch((err) => {
      console.debug('[analytics] track/view fetch failed:', err?.message);
    });
  } catch (err) {
    console.debug('[analytics] trackPageView failed:', err?.message);
  }
};

export const trackViewItem = (product) => {
  push({
    event: 'view_item',
    ecommerce: {
      currency: 'SEK',
      value: Number(product?.price) || 0,
      items: [toItem(product)],
    },
  });
};

export const trackAddToCart = (item) => {
  push({
    event: 'add_to_cart',
    ecommerce: {
      currency: 'SEK',
      value: (Number(item?.price) || 0) * (Number(item?.quantity) || 1),
      items: [toItem(item)],
    },
  });
};

export const trackRemoveFromCart = (item) => {
  push({
    event: 'remove_from_cart',
    ecommerce: {
      currency: 'SEK',
      value: (Number(item?.price) || 0) * (Number(item?.quantity) || 1),
      items: [toItem(item)],
    },
  });
};

export const trackBeginCheckout = (cartItems, total) => {
  push({
    event: 'begin_checkout',
    ecommerce: {
      currency: 'SEK',
      value: Number(total) || 0,
      items: (cartItems || []).map((it, i) => toItem(it, i)),
    },
  });
};

// GA4 Enhanced Ecommerce funnel: fires when the customer has entered valid
// shipping info (name/address/postal_code) on the /kassa page. Used by
// Google Ads Smart Bidding as a mid-funnel signal.
export const trackAddShippingInfo = (cartItems, total, shippingTier = 'Standard') => {
  push({
    event: 'add_shipping_info',
    ecommerce: {
      currency: 'SEK',
      value: Number(total) || 0,
      shipping_tier: shippingTier,
      items: (cartItems || []).map((it, i) => toItem(it, i)),
    },
  });
};

// GA4 Enhanced Ecommerce funnel: fires when the customer has selected a
// payment method (card/klarna/swish) — the "step before purchase" signal.
export const trackAddPaymentInfo = (cartItems, total, paymentType = 'card') => {
  push({
    event: 'add_payment_info',
    ecommerce: {
      currency: 'SEK',
      value: Number(total) || 0,
      payment_type: paymentType,
      items: (cartItems || []).map((it, i) => toItem(it, i)),
    },
  });
};

export const trackPurchase = (order) => {
  if (!order) return;
  push({
    event: 'purchase',
    ecommerce: {
      transaction_id: order.order_id || order.session_id || `${Date.now()}`,
      value: Number(order.total_amount || order.total) || 0,
      currency: 'SEK',
      shipping: Number(order.shipping_cost) || 0,
      tax: Number(order.tax_amount) || 0,
      items: (order.items || []).map((it, i) => toItem(it, i)),
    },
  });
};
